import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/app/ProtectedRoute";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import NewProject from "@/pages/NewProject";
import ProjectEnterprise from "@/pages/ProjectEnterprise";
import ProjectOutput from "@/pages/ProjectOutput";
import ProjectShare from "@/pages/ProjectShare";
import PlaceholderPage from "@/pages/Placeholder";
import RichPage from "@/pages/RichPage";
import Pricing from "@/pages/Pricing";
import BillingSuccess from "@/pages/BillingSuccess";
import Templates from "@/pages/Templates";
import Settings from "@/pages/Settings";
import Resources from "@/pages/Resources";
import { STATIC_PAGES } from "@/lib/staticPages";

function LandingOrRedirect() {
  const { user } = useAuth();
  if (user && user !== false && user !== null) return <Navigate to="/dashboard" replace />;
  return <Landing />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<LandingOrRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route
              path="/billing/success"
              element={
                <ProtectedRoute>
                  <BillingSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates"
              element={
                <ProtectedRoute>
                  <Templates />
                </ProtectedRoute>
              }
            />
            <Route
              path="/resources"
              element={
                <ProtectedRoute>
                  <Resources />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/new"
              element={
                <ProtectedRoute>
                  <NewProject />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:id"
              element={
                <ProtectedRoute>
                  <ProjectIdRedirect />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:id/enterprise"
              element={
                <ProtectedRoute>
                  <ProjectEnterprise />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:id/output"
              element={
                <ProtectedRoute>
                  <ProjectOutput />
                </ProtectedRoute>
              }
            />
            <Route path="/share/:token" element={<ProjectShare />} />
            {STATIC_PAGES.map((p) => (
              <Route
                key={p.path}
                path={p.path}
                element={
                  p.sections && p.sections.length > 0 ? (
                    <RichPage
                      section={p.section}
                      title={p.title}
                      lead={p.description}
                      pill={p.pill}
                      cta={p.cta}
                      sections={p.sections}
                    />
                  ) : (
                    <PlaceholderPage
                      section={p.section}
                      title={p.title}
                      description={p.description}
                    />
                  )
                }
              />
            ))}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster
            position="bottom-right"
            theme="dark"
            richColors
            closeButton
            duration={4000}
            toastOptions={{
              style: {
                background: "var(--color-background-secondary)",
                border: "1px solid var(--color-border-primary)",
                color: "var(--color-text-primary)",
                borderRadius: 12,
                fontFamily: "Inter, system-ui, sans-serif",
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;

// Redirect old `/projects/:id` URLs to the new canonical `/output` view.
// Kept as a tiny shim so bookmarks and "View conversation" links continue to
// resolve after the legacy ConceptualCanvas page was removed.
function ProjectIdRedirect() {
  const { id } = useParams();
  return <Navigate to={`/projects/${id}/output`} replace />;
}
